export const THEMES = ["system", "light", "dark"] as const
export type ThemeMode = (typeof THEMES)[number]

export const DEFAULT_THEME: ThemeMode = "system"
export const THEME_STORAGE_KEY = "formatloom-theme"

export function normalizeTheme(value: unknown): ThemeMode {
  return typeof value === "string" && THEMES.includes(value as ThemeMode)
    ? (value as ThemeMode)
    : DEFAULT_THEME
}

export function readStoredTheme(storage: Storage | null | undefined): ThemeMode {
  try {
    return normalizeTheme(storage?.getItem(THEME_STORAGE_KEY))
  } catch {
    return DEFAULT_THEME
  }
}

export function persistTheme(
  storage: Storage | null | undefined,
  theme: ThemeMode,
): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Browser privacy settings can make localStorage unavailable.
  }
}

export function effectiveTheme(theme: ThemeMode, prefersDark: boolean): "light" | "dark" {
  return theme === "system" ? (prefersDark ? "dark" : "light") : theme
}

export function applyTheme(
  documentObject: Document,
  theme: ThemeMode,
  prefersDark = false,
): void {
  const root = documentObject.documentElement
  root.dataset.theme = theme
  root.style.colorScheme = effectiveTheme(theme, prefersDark)
}

type ThemeWindow = Pick<Window, "localStorage" | "matchMedia">

function browserStorage(windowObject: ThemeWindow): Storage | null {
  try {
    return windowObject.localStorage
  } catch {
    return null
  }
}

export function initializeTheme(
  documentObject: Document,
  windowObject: ThemeWindow,
): () => void {
  const mediaQuery = windowObject.matchMedia("(prefers-color-scheme: dark)")
  const storage = browserStorage(windowObject)
  let currentTheme = readStoredTheme(storage)

  const render = (): void => {
    applyTheme(documentObject, currentTheme, mediaQuery.matches)
    documentObject.querySelectorAll<HTMLElement>("[data-theme-mode]").forEach((button) => {
      const active = button.dataset.themeMode === currentTheme
      button.setAttribute("aria-pressed", String(active))
      button.classList.toggle("is-active", active)
    })
  }

  const handleClick = (event: Event): void => {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-theme-mode]")
    if (!button) return

    const nextTheme = normalizeTheme(button.dataset.themeMode)
    currentTheme = nextTheme
    persistTheme(storage, currentTheme)
    render()
  }

  const handleSystemChange = (): void => {
    if (currentTheme === "system") render()
  }

  documentObject.addEventListener("click", handleClick)
  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", handleSystemChange)
  } else {
    mediaQuery.addListener(handleSystemChange)
  }

  render()

  return () => {
    documentObject.removeEventListener("click", handleClick)
    if (typeof mediaQuery.removeEventListener === "function") {
      mediaQuery.removeEventListener("change", handleSystemChange)
    } else {
      mediaQuery.removeListener(handleSystemChange)
    }
  }
}
